import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, CheckCircle, ArrowRight, Truck, Shield, Zap, Tag } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";

const GUIDE_PACKAGES = [
  { id: "pack_5", guides: 5, price_per_guide: 89.00, discount: 0, label: "Starter" },
  { id: "pack_10", guides: 10, price_per_guide: 79.00, discount: 11, label: "Básico" },
  { id: "pack_25", guides: 25, price_per_guide: 69.00, discount: 22, label: "Popular", featured: true },
  { id: "pack_50", guides: 50, price_per_guide: 62.00, discount: 30, label: "Empresarial" },
];

const PrepaidGuidesPage = () => {
  const { user } = useAuth();
  const [purchasing, setPurchasing] = useState(null);

  const handlePurchase = async (pkg) => {
    if (!user) {
      toast.error("Inicia sesión para comprar guías prepago");
      return;
    }
    setPurchasing(pkg.id);
    try {
      const token = sessionStorage.getItem("token");
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/guides`, {
        origin_url: originUrl,
        package_id: pkg.id
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al procesar la compra");
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="prepaid-guides-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 bg-[#F57C00] text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
              <Tag size={16} /> Hasta 30% de descuento
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#5D4037]">
              Guías de Envío <span className="text-[#F57C00]">Prepago</span>
            </h1>
            <p className="text-stone-600 text-lg mt-4 max-w-2xl mx-auto">
              Compra paquetes de guías con descuento por volumen. Sin fecha de vencimiento. Genera guías cuando las necesites.
            </p>
          </motion.div>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: CreditCard, title: "Pago único", desc: "Compra una vez, usa cuando quieras" },
            { icon: Shield, title: "Sin vencimiento", desc: "Tus guías no caducan nunca" },
            { icon: Truck, title: "Todas las paqueterías", desc: "DHL, FedEx, UPS y más" },
            { icon: Zap, title: "Generación instant.", desc: "Crea tu guía en segundos" },
          ].map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="bento-card p-5 text-center" data-testid={`guide-benefit-${i}`}>
              <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <b.icon size={24} className="text-[#2E7D32]" />
              </div>
              <p className="font-bold text-[#5D4037] text-sm">{b.title}</p>
              <p className="text-xs text-stone-500 mt-1">{b.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {GUIDE_PACKAGES.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`bento-card p-6 relative ${pkg.featured ? "ring-2 ring-[#F57C00] bg-[#F57C00]/5" : ""}`}
              data-testid={`guide-package-${pkg.id}`}
            >
              {pkg.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F57C00] text-white text-xs font-bold px-3 py-1 rounded-full">
                  MÁS POPULAR
                </span>
              )}
              <div className="text-center">
                <p className="font-mono text-xs text-stone-500 tracking-wider uppercase">{pkg.label}</p>
                <p className="text-5xl font-black text-[#5D4037] mt-3">{pkg.guides}</p>
                <p className="text-sm text-stone-500">guías</p>

                <div className="my-4 py-4 border-t border-b border-stone-100">
                  <p className="text-3xl font-black text-[#2E7D32]">${(pkg.guides * pkg.price_per_guide).toLocaleString("es-MX")}</p>
                  <p className="text-sm text-stone-500">MXN total</p>
                  <p className="text-xs text-stone-400 mt-1">${pkg.price_per_guide}/guía</p>
                </div>

                {pkg.discount > 0 && (
                  <div className="inline-flex items-center gap-1 bg-[#4CAF50]/10 text-[#2E7D32] px-3 py-1 rounded-full text-sm font-bold mb-4">
                    <Tag size={14} /> {pkg.discount}% descuento
                  </div>
                )}

                <ul className="space-y-2 text-left mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600"><CheckCircle size={16} className="text-[#4CAF50]" /> {pkg.guides} guías prepago</li>
                  <li className="flex items-center gap-2 text-sm text-stone-600"><CheckCircle size={16} className="text-[#4CAF50]" /> Sin fecha de vencimiento</li>
                  <li className="flex items-center gap-2 text-sm text-stone-600"><CheckCircle size={16} className="text-[#4CAF50]" /> Todas las paqueterías</li>
                  {pkg.discount >= 20 && (
                    <li className="flex items-center gap-2 text-sm text-stone-600"><CheckCircle size={16} className="text-[#4CAF50]" /> Soporte prioritario</li>
                  )}
                </ul>

                <button
                  onClick={() => handlePurchase(pkg)}
                  disabled={purchasing === pkg.id}
                  className={`w-full font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                    pkg.featured
                      ? "bg-[#F57C00] text-white hover:bg-[#E65100]"
                      : "bg-[#2E7D32] text-white hover:bg-[#1B5E20]"
                  }`}
                  data-testid={`buy-package-${pkg.id}`}
                >
                  {purchasing === pkg.id ? "Procesando..." : <><CreditCard size={18} /> Comprar</>}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* How it works */}
        <div className="bento-card p-8 md:p-12 mb-12">
          <h2 className="text-2xl font-bold text-[#5D4037] text-center mb-8">¿Cómo funciona?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Elige tu paquete", desc: "Selecciona el paquete que mejor se ajuste a tu volumen de envíos." },
              { step: "2", title: "Paga en línea", desc: "Pago seguro con tarjeta de crédito/débito vía Stripe." },
              { step: "3", title: "Genera tus guías", desc: "Desde tu panel, genera guías con cualquier paquetería cuando lo necesites." },
            ].map((item, i) => (
              <div key={i} className="text-center" data-testid={`how-step-${i}`}>
                <div className="w-12 h-12 bg-[#F57C00] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-black">{item.step}</div>
                <h3 className="font-bold text-[#5D4037] mb-2">{item.title}</h3>
                <p className="text-sm text-stone-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        {!user && (
          <div className="text-center">
            <p className="text-stone-600 mb-4">Necesitas una cuenta para comprar guías prepago</p>
            <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="guides-login-cta">
              Crear Cuenta <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrepaidGuidesPage;
